import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import { useAuth } from "@/context/AuthContext";
import {
  getQuestion,
  createAnswer,
  toggleUpvote,
  acceptAnswer,
  type Answer,
} from "@/api/questions";

export default function QuestionDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [answerBody, setAnswerBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["question", id],
    queryFn: () => getQuestion(id!),
    enabled: !!id,
  });

  const q = data?.data;
  // `isOwner` comes from the server now. Comparing ids on the client cannot
  // work for an anonymous question — the author is stripped from the response,
  // which is the point of it.
  const isAsker = q?.isOwner ?? false;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["question", id] });
  }

  async function postAnswer() {
    if (!q || answerBody.trim().length < 10) return;
    setBusy(true);
    setFeedback(null);
    try {
      await createAnswer(q.id, answerBody.trim());
      setAnswerBody("");
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Failed to post answer");
    } finally {
      setBusy(false);
    }
  }

  async function upvote(answerId: string) {
    try {
      await toggleUpvote(answerId);
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Upvote failed");
    }
  }

  async function accept(answerId: string) {
    try {
      await acceptAnswer(answerId);
      setFeedback("Answer accepted.");
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Accept failed");
    }
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
        Failed to load question.{" "}
        <button onClick={() => refetch()} className="underline font-medium">
          Retry
        </button>
      </div>
    );
  if (!q) return <p className="text-sm text-gray-500">Not found.</p>;

  return (
    <div className="space-y-6">
      <PageHeader
        title={q.title}
        subtitle={`Asked by ${q.isAnonymous ? "someone, anonymously" : (q.user?.name ?? "—")} · ${q.answerCount} answers · ${q.viewCount} views`}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Curios", href: "/lab/questions" },
          { label: q.title.length > 40 ? q.title.slice(0, 40) + "…" : q.title },
        ]}
      />

      {feedback && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-3 py-2 rounded">
          {feedback}
        </div>
      )}

      {/* Question body */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {q.isAnswered && <Badge variant="green">answered</Badge>}
          {q.category && <Badge variant="gray">{q.category.name}</Badge>}
          {q.tags.map((t) => (
            <Badge key={t} variant="blue">
              {t}
            </Badge>
          ))}
        </div>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{q.body}</p>
      </section>

      {/* Answers */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">
          {q.answers.length} {q.answers.length === 1 ? "Answer" : "Answers"}
        </h2>
        {q.answers.map((a) => (
          <AnswerCard
            key={a.id}
            a={a}
            canAccept={isAsker && !a.isAccepted}
            canUpvote={isAuthenticated}
            onUpvote={() => upvote(a.id)}
            onAccept={() => accept(a.id)}
          />
        ))}
        {q.answers.length === 0 && (
          <p className="text-sm text-gray-500">No answers yet.</p>
        )}
      </section>

      {/* Post answer */}
      {isAuthenticated && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Your answer</h3>
          <TextAreaField
            label=""
            rows={4}
            placeholder="At least 10 characters"
            value={answerBody}
            onChange={(e) => setAnswerBody(e.target.value)}
          />
          <div className="mt-3">
            <Button
              size="sm"
              onClick={postAnswer}
              isLoading={busy}
              disabled={answerBody.trim().length < 10}
            >
              Post answer
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function AnswerCard({
  a,
  canAccept,
  canUpvote,
  onUpvote,
  onAccept,
}: {
  a: Answer;
  canAccept: boolean;
  canUpvote: boolean;
  onUpvote: () => void;
  onAccept: () => void;
}) {
  return (
    <div
      className={`bg-white border rounded-2xl p-4 ${a.isAccepted ? "border-green-300 ring-1 ring-green-100" : "border-gray-200"}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="font-medium text-gray-900">{a.business?.name ?? a.user?.name}</span>
        {a.business && <Badge variant="blue">the business</Badge>}
        {a.isAccepted && <Badge variant="green">accepted ✓</Badge>}
        <span className="text-xs text-gray-400">
          {new Date(a.createdAt).toLocaleDateString()}
        </span>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.body}</p>

      {a.mentionedProviders.length > 0 && (
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {a.mentionedProviders.map((p) => (
            <Badge key={p.id} variant="purple">
              @{p.name}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <Button
          size="sm"
          variant={a.hasUpvoted ? "primary" : "secondary"}
          onClick={onUpvote}
          disabled={!canUpvote}
        >
          ▲ {a.upvoteCount}
        </Button>
        {canAccept && (
          <Button size="sm" variant="secondary" onClick={onAccept}>
            Accept
          </Button>
        )}
      </div>
    </div>
  );
}
